import React from 'react';
import { UserRole, AccountTier } from '../types/user';
import { ShieldCheck, Shield, Crown, User, Check, Lock } from 'lucide-react';

interface RoleComparisonViewProps {
  lang?: string;
}

interface RoleMappingItem {
  displayLabel: string;
  enumValue: UserRole;
  tierMapping: string;
  adminPortalAccess: boolean;
  kundaliVaultAccess: string;
  astrologyDepth: string;
  consultationAccess: boolean;
}

const ROLE_MAPPINGS: RoleMappingItem[] = [
  {
    displayLabel: 'SUPER_ADMIN',
    enumValue: UserRole.SUPER_ADMIN,
    tierMapping: 'Management (Full System Governance)',
    adminPortalAccess: true,
    kundaliVaultAccess: 'Cross-User Vault (All Records)',
    astrologyDepth: 'Full Complete Vedic Suite (D1-D60, Ashtakvarga, KP, Shadbala)',
    consultationAccess: true
  },
  {
    displayLabel: 'ADMIN',
    enumValue: UserRole.ADMIN,
    tierMapping: 'Management (User Maintenance & Support)',
    adminPortalAccess: true,
    kundaliVaultAccess: 'Admin & Support Oversight',
    astrologyDepth: 'Full Complete Vedic Suite',
    consultationAccess: true
  },
  {
    displayLabel: 'CONSULTING_ASTROLOGER',
    enumValue: UserRole.CONSULTING_ASTROLOGER,
    tierMapping: 'Acharya (Client Consultation Workbench)',
    adminPortalAccess: false,
    kundaliVaultAccess: 'Client Practice Vault',
    astrologyDepth: 'Acharya Muhurta, Remedies & Client Synthesis',
    consultationAccess: true
  },
  {
    displayLabel: 'PRO_ASTROLOGER',
    enumValue: UserRole.PRO_ASTROLOGER,
    tierMapping: 'Pro Astro (Divisional & KP System)',
    adminPortalAccess: false,
    kundaliVaultAccess: 'Professional Practice Records',
    astrologyDepth: 'Divisional D1-D60, Ashtakvarga, Shadbala, Transit Telemetry',
    consultationAccess: false
  },
  {
    displayLabel: 'NOVICE_ASTROLOGER',
    enumValue: UserRole.NOVICE_ASTROLOGER,
    tierMapping: 'Pro Astro / Learner',
    adminPortalAccess: false,
    kundaliVaultAccess: 'Personal & Learning Records',
    astrologyDepth: 'Advanced Calculations & Learning Explanations',
    consultationAccess: false
  },
  {
    displayLabel: 'ADVANCED_ASTROLOGY_USER',
    enumValue: UserRole.ADVANCED_ASTROLOGY_USER,
    tierMapping: 'Pro Astro / Advanced User',
    adminPortalAccess: false,
    kundaliVaultAccess: 'Personal Dossier',
    astrologyDepth: 'Advanced Predictions & Technical Reasoning',
    consultationAccess: false
  },
  {
    displayLabel: 'BASIC_USER',
    enumValue: UserRole.BASIC_USER,
    tierMapping: 'Basic Profile',
    adminPortalAccess: false,
    kundaliVaultAccess: 'Own Personal Jataka Dossier',
    astrologyDepth: 'Foundational Planetary Positions & Life Questions',
    consultationAccess: false
  },
  {
    displayLabel: 'GUEST',
    enumValue: UserRole.GUEST,
    tierMapping: 'Guest Session',
    adminPortalAccess: false,
    kundaliVaultAccess: 'Session-only / Temporary',
    astrologyDepth: 'Basic Calculations',
    consultationAccess: false
  },
  {
    displayLabel: 'TESTER',
    enumValue: UserRole.TESTER,
    tierMapping: 'Sandbox Testing / Simulator',
    adminPortalAccess: true,
    kundaliVaultAccess: 'Testing & QA Sandbox',
    astrologyDepth: 'Full Suite Sandbox',
    consultationAccess: true
  }
];

export const RoleComparisonView: React.FC<RoleComparisonViewProps> = ({ lang = 'en' }) => {
  const isHi = lang === 'hi';

  return (
    <div id="role-comparison-view" className="space-y-6 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-astro-border">
        <div>
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-astro-primary" />
            <h3 className="text-base font-bold text-astro-primary font-serif">
              {isHi ? 'भूमिका तुलना मैट्रिक्स (Role Comparison Matrix)' : 'Role Elevation vs. Implementation Matrix'}
            </h3>
          </div>
          <p className="text-xs text-astro-muted">
            Comprehensive audit mapping between Admin Portal role elevation dropdown options and actual `UserRole` implementation enums & access controls.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-astro-border shadow-xs bg-white">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-[#FAF6F0] text-astro-secondary-text font-bold border-b border-astro-border">
            <tr>
              <th className="p-3">Admin Portal Dropdown Option</th>
              <th className="p-3">Actual `UserRole` Enum</th>
              <th className="p-3">Workstation Tier</th>
              <th className="p-3">Admin Portal Access</th>
              <th className="p-3">Kundali Vault Scope</th>
              <th className="p-3">Astrological Analysis Depth</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-astro-border">
            {ROLE_MAPPINGS.map((item) => (
              <tr key={item.enumValue} className="hover:bg-[#FAF6F0]/50 transition-colors">
                <td className="p-3 font-mono font-bold text-astro-primary">
                  {item.displayLabel}
                </td>
                <td className="p-3 font-mono text-[11px] text-astro-accent">
                  {item.enumValue}
                </td>
                <td className="p-3 font-semibold text-astro-text">
                  {item.tierMapping}
                </td>
                <td className="p-3">
                  {item.adminPortalAccess ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 text-[10px] font-bold border border-emerald-200">
                      <Check className="w-3 h-3 text-emerald-600" />
                      <span>Allowed</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-stone-100 text-stone-600 text-[10px] font-medium border border-stone-200">
                      <Lock className="w-3 h-3 text-stone-500" />
                      <span>Restricted</span>
                    </span>
                  )}
                </td>
                <td className="p-3 text-[11px] text-astro-muted">
                  {item.kundaliVaultAccess}
                </td>
                <td className="p-3 text-[11px] text-astro-text">
                  {item.astrologyDepth}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
